import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../Environment';
import useRetryFetch from '../../../hooks/useRetryFetch';
import useAuth from '../../../hooks/useAuth';
import LoggerContext from '../../../context/LoggerProvider';
import { useOrgAdminStatus, useMentorCollectionsQuery } from '../../../hooks/usePortalData';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { FolderTree, Users, Search, ChevronDown, ChevronRight, BarChart3, Building2 } from 'lucide-react';
import { Skeleton } from '../../ui/skeleton';
import { Spinner } from '../../ui/spinner';
import GroupReportModal from './GroupReportModal';
import OrganizationOverviewModal from './OrganizationOverviewModal';

interface Collection {
  _id: string;
  name: string;
  level: number;
  fiUsers: any[];
  subCollections: any[];
  mentorRole: string; // role assigned to this mentor
}

interface Student {
  _id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  processCount?: number;
}

const MyGroups = () => {
  const { t } = useTranslation();
  const { auth, user } = useAuth();
  const { Logger } = useContext(LoggerContext);
  const retryFetch = useRetryFetch();

  // Use shared React Query hooks — deduplicates with Portal's fetches
  const { orgAdminOrgs: organizations } = useOrgAdminStatus();
  const { data: mentorCollectionsData, isLoading: loading } = useMentorCollectionsQuery();
  const collections = (mentorCollectionsData?.collections || []) as Collection[];

  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [studentsData, setStudentsData] = useState<Record<string, Student[]>>({});
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [orgOverviewModalOpen, setOrgOverviewModalOpen] = useState(false);

  const fetchCollectionUsers = async (collectionId: string) => {
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/collections/getusers/${collectionId}`
      );

      if (response.ok) {
        const data = await response.json();
        setStudentsData(prev => ({ ...prev, [collectionId]: data }));
      }
    } catch (error) {
      Logger.error('Failed to fetch collection users:', error);
      toast.error(t('failed_to_load_data'));
    }
  };

  const toggleCollection = (collectionId: string) => {
    const newExpanded = new Set(expandedCollections);
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId);
    } else {
      newExpanded.add(collectionId);
      // Fetch users if not already loaded
      if (!studentsData[collectionId]) {
        fetchCollectionUsers(collectionId);
      }
    }
    setExpandedCollections(newExpanded);
  };

  const getLevelLabel = (level: number) => {
    if (level === 10) return t('department');
    if (level === 20) return t('course');
    if (level === 30) return t('class');
    return `${t('level')} ${level}`;
  };

  const getDisplayName = (student: Student) => {
    if (student.displayName) return student.displayName;
    if (student.firstName || student.lastName) {
      return [student.firstName, student.lastName].filter(Boolean).join(' ');
    }
    return student.email?.split('@')[0] || 'N/A';
  };

  const openReportModal = (collection: Collection) => {
    setSelectedCollection(collection);
    setReportModalOpen(true);
  };

  const filteredCollections = collections.filter(coll =>
    coll.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <FolderTree className="mx-auto h-12 w-12 mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">{t('no_groups_assigned')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('no_groups_assigned_description')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t('my_groups')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('my_groups_description')}
          </p>
        </div>
        <Badge variant="secondary" className="text-lg px-4 py-2">
          {collections.length} {t('groups')}
        </Badge>
      </div>

      {/* Organization Overview Button (only for org admins) */}
      {organizations.length > 0 && (
        <Card className="border-2 border-primary/20 bg-primary/5 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer">
          <CardContent className="p-4" onClick={() => setOrgOverviewModalOpen(true)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{t('organization_overview')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('view_organization_statistics_and_breakdown')}
                  </p>
                </div>
              </div>
              <Button variant="default" size="sm">
                <BarChart3 className="mr-2 h-4 w-4" />
                {t('view_report')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder={t('search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Groups List */}
      <div className="space-y-4">
        {filteredCollections.map((collection) => {
          const isExpanded = expandedCollections.has(collection._id);
          const students = studentsData[collection._id] || [];
          const loadingStudents = isExpanded && !studentsData[collection._id];

          return (
            <Card
              key={collection._id}
              className="overflow-hidden border-2 hover:border-primary/50 hover:shadow-md transition-all duration-200"
            >
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-3 flex-1"
                    onClick={() => toggleCollection(collection._id)}
                  >
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="flex items-center gap-2 flex-wrap">
                        <FolderTree className="h-5 w-5 text-primary shrink-0" />
                        <span className="truncate">{collection.name}</span>
                        <Badge variant="outline" className="shrink-0">
                          {getLevelLabel(collection.level)}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="mt-2 flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span className="font-medium">{collection.fiUsers.length}</span>{' '}
                          {t('students')}
                        </span>
                        {collection.subCollections.length > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <FolderTree className="h-3 w-3" />
                            <span className="font-medium">{collection.subCollections.length}</span>{' '}
                            {t('sub_collections')}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openReportModal(collection);
                      }}
                      className="hidden md:flex"
                    >
                      <BarChart3 className="mr-2 h-4 w-4" />
                      {t('view_progress')}
                    </Button>
                    <Badge variant="secondary" className="px-3 py-1">
                      {collection.mentorRole || t('mentor')}
                    </Badge>
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="border-t bg-muted/20 pt-4">
                  {loadingStudents ? (
                    <div className="flex items-center justify-center gap-2 py-8">
                      <Spinner size="md" />
                      <span>{t('loading')}</span>
                    </div>
                  ) : students.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h4 className="text-sm font-semibold">{t('students')}</h4>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openReportModal(collection);
                          }}
                          className="md:hidden"
                        >
                          <BarChart3 className="mr-2 h-4 w-4" />
                          {t('view_progress')}
                        </Button>
                      </div>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('name')}</TableHead>
                              <TableHead>{t('email')}</TableHead>
                              <TableHead>{t('status')}</TableHead>
                              <TableHead className="text-right">{t('processes')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {students.map((student) => (
                              <TableRow key={student._id}>
                                <TableCell className="font-medium">
                                  {getDisplayName(student)}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {student.email}
                                </TableCell>
                                <TableCell>
                                  <Badge variant={student.isActive ? 'default' : 'secondary'}>
                                    {student.isActive ? t('active') : t('inactive')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  {student.processCount || 0}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <Users className="mx-auto h-8 w-8 mb-2 opacity-50" />
                      <p className="text-sm">{t('no_users_in_collection')}</p>
                    </div>
                  )}

                  {collection.subCollections.length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="text-sm font-semibold mb-3">{t('sub_collections')}</h4>
                      <div className="grid gap-2">
                        {collection.subCollections.map((sub: any) => (
                          <div
                            key={sub._id || sub}
                            className="flex items-center justify-between p-3 rounded-lg border bg-background"
                          >
                            <div className="flex items-center gap-2">
                              <FolderTree className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                {sub.name || t('subgroup')}
                              </span>
                            </div>
                            {sub.level && (
                              <Badge variant="outline" className="text-xs">
                                {getLevelLabel(sub.level)}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {filteredCollections.length === 0 && searchQuery && (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">{t('no_groups_found')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
        <Card className="border-2 hover:border-primary/30 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <FolderTree className="h-4 w-4 text-primary" />
              {t('total_groups')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{collections.length}</div>
          </CardContent>
        </Card>
        <Card className="border-2 hover:border-primary/30 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4 text-primary" />
              {t('total_students')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {collections.reduce((acc, c) => acc + c.fiUsers.length, 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 hover:border-primary/30 transition-colors">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <FolderTree className="h-4 w-4 text-primary" />
              {t('sub_collections')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {collections.reduce((acc, c) => acc + c.subCollections.length, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Modals */}
      {selectedCollection && (
        <GroupReportModal
          open={reportModalOpen}
          onOpenChange={setReportModalOpen}
          collectionId={selectedCollection._id}
          collectionName={selectedCollection.name}
          collectionLevel={selectedCollection.level}
        />
      )}

      {organizations.length > 0 && (
        <OrganizationOverviewModal
          open={orgOverviewModalOpen}
          onOpenChange={setOrgOverviewModalOpen}
          organizations={organizations}
        />
      )}
    </div>
  );
};

export default MyGroups;
